import React from "react";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Divider from "@amzn/meridian/divider";
import { GetShiftPlanOutputResponse } from "../../../reviewAndPublish/model";
import { PlanPdfMetadata } from "./downloadPlanPdf";
import DownloadPlanButton from "./DownloadPlanButton";

/**
 * Plan Actions card (UTR-REFLEX-1163) — sits below the Process Path table. Holds
 * the Download Plan action and a "Last generated by <name> at <date>" line with
 * an initials avatar. Meridian components only; V2 sibling.
 */
interface PlanActionsCardProps {
  shiftPlanOutput?: GetShiftPlanOutputResponse;
  pdfMetadata: PlanPdfMetadata;
  /** Display name of who last generated the selected plan. */
  lastGeneratedBy?: string;
  /** Formatted "last generated at" timestamp for the selected plan. */
  lastGeneratedAt?: string;
}

// 1–2 character initials from a display name (e.g. "Jane Smith" -> "JS").
const getInitials = (name?: string): string => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
};

const PlanActionsCard: React.FC<PlanActionsCardProps> = ({
  shiftPlanOutput,
  pdfMetadata,
  lastGeneratedBy,
  lastGeneratedAt,
}) => {
  return (
    <Box type="outline" spacingInset="500">
      <Column spacing="400">
        <Heading type="h500" level={2}>Plan Actions</Heading>

        <Row>
          <DownloadPlanButton shiftPlanOutput={shiftPlanOutput} metadata={pdfMetadata} />
        </Row>

        {(lastGeneratedBy || lastGeneratedAt) && (
          <>
            <Divider size="small" />
            <Row spacing="300" alignmentVertical="center">
              {/* Circular initials avatar. Meridian has no avatar primitive, so a
                  Box with a subdued fill approximates the mock's chip; an inner
                  Column centers the initials both axes within the fixed size. */}
              <Box width={40} height={40} backgroundColor="subdued" type="outline">
                <Column height="100%" alignmentHorizontal="center" alignmentVertical="center">
                  <Text type="b200">{getInitials(lastGeneratedBy)}</Text>
                </Column>
              </Box>
              <Column spacing="none">
                <Text type="b300">
                  Last generated by <b>{lastGeneratedBy ?? "Unknown"}</b>
                </Text>
                {lastGeneratedAt && (
                  <Text type="b200" color="secondary">{lastGeneratedAt}</Text>
                )}
              </Column>
            </Row>
          </>
        )}
      </Column>
    </Box>
  );
};

export default PlanActionsCard;
